const MS_PER_MINUTE = 60 * 1000;
const MINUTES_PER_DAY = 24 * 60;

const FALLBACK_TIMEZONES = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

let timezoneOptionsCache;

export function getBrowserTimezone() {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone && isValidTimezone(timezone) ? timezone : "UTC";
  } catch (_error) {
    return "UTC";
  }
}

export function isValidTimezone(timezone) {
  if (!timezone || typeof timezone !== "string") return false;

  try {
    new Intl.DateTimeFormat("en-US", { timeZone: timezone }).format(new Date());
    return true;
  } catch (_error) {
    return false;
  }
}

export function getTimezoneOptions() {
  if (timezoneOptionsCache) return timezoneOptionsCache;

  const supported =
    typeof Intl.supportedValuesOf === "function"
      ? Intl.supportedValuesOf("timeZone")
      : FALLBACK_TIMEZONES;

  timezoneOptionsCache = Array.from(new Set(["UTC", ...supported, ...FALLBACK_TIMEZONES])).filter(
    isValidTimezone
  );

  return timezoneOptionsCache;
}

export function normalizeTimezone(timezone) {
  return isValidTimezone(timezone) ? timezone : getBrowserTimezone();
}

export function getDateFromFirestoreValue(value) {
  if (!value) return null;

  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value.seconds === "number") {
    const date = new Date(value.seconds * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  return null;
}

function parseLocalDateTime(localDateTime) {
  if (!localDateTime || typeof localDateTime !== "string") return null;

  const match = localDateTime
    .trim()
    .match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText = "0"] = match;
  const [year, month, day, hour, minute, second] = [
    yearText,
    monthText,
    dayText,
    hourText,
    minuteText,
    secondText,
  ].map(Number);
  const utcCheck = new Date(Date.UTC(year, month - 1, day, hour, minute, second));

  const isRealDate =
    utcCheck.getUTCFullYear() === year &&
    utcCheck.getUTCMonth() === month - 1 &&
    utcCheck.getUTCDate() === day &&
    utcCheck.getUTCHours() === hour &&
    utcCheck.getUTCMinutes() === minute &&
    utcCheck.getUTCSeconds() === second;

  return isRealDate ? { year, month, day, hour, minute, second } : null;
}

function getZonedDateParts(date, timezone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(date);

  return parts.reduce((values, part) => {
    if (part.type !== "literal") values[part.type] = Number(part.value);
    return values;
  }, {});
}

function getTimezoneOffsetMs(date, timezone) {
  const parts = getZonedDateParts(date, timezone);
  const zonedAsUtc = Date.UTC(
    parts.year,
    parts.month - 1,
    parts.day,
    parts.hour,
    parts.minute,
    parts.second || 0
  );
  return zonedAsUtc - date.getTime();
}

export function getUtcDateForZonedLocalDateTime(localDateTime, timezone) {
  const parsed = parseLocalDateTime(localDateTime);
  const normalizedTimezone = normalizeTimezone(timezone);
  if (!parsed || !isValidTimezone(normalizedTimezone)) return null;

  const targetLocalAsUtc = Date.UTC(
    parsed.year,
    parsed.month - 1,
    parsed.day,
    parsed.hour,
    parsed.minute,
    parsed.second || 0
  );

  let utcMs = targetLocalAsUtc;
  for (let i = 0; i < 3; i += 1) {
    utcMs = targetLocalAsUtc - getTimezoneOffsetMs(new Date(utcMs), normalizedTimezone);
  }

  const resolved = new Date(utcMs);
  const verification = getZonedDateParts(resolved, normalizedTimezone);
  const matchesRequestedWallClock =
    verification.year === parsed.year &&
    verification.month === parsed.month &&
    verification.day === parsed.day &&
    verification.hour === parsed.hour &&
    verification.minute === parsed.minute;

  return matchesRequestedWallClock ? resolved : null;
}

export function toDateTimeLocalInputValue(value, timezone = getBrowserTimezone()) {
  const date = getDateFromFirestoreValue(value);
  const normalizedTimezone = normalizeTimezone(timezone);
  if (!date) return "";

  const parts = getZonedDateParts(date, normalizedTimezone);
  const pad = (number) => String(number).padStart(2, "0");
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(
    parts.minute
  )}`;
}

export function getScheduleStartDate(schedule) {
  if (!schedule?.enabled) return null;

  const storedStart = getDateFromFirestoreValue(schedule.start_at);
  if (storedStart) return storedStart;

  return getUtcDateForZonedLocalDateTime(schedule.start_local, schedule.timezone);
}

export function calculateCountdownParts(targetValue, nowValue = new Date()) {
  const target = getDateFromFirestoreValue(targetValue);
  const now = getDateFromFirestoreValue(nowValue) || new Date();
  if (!target) return null;

  const diffMs = target.getTime() - now.getTime();
  const totalMinutes = diffMs <= 0 ? 0 : Math.ceil(diffMs / MS_PER_MINUTE);
  const days = Math.floor(totalMinutes / MINUTES_PER_DAY);
  const remainingAfterDays = totalMinutes - days * MINUTES_PER_DAY;
  const hours = Math.floor(remainingAfterDays / 60);
  const minutes = remainingAfterDays % 60;

  return { days, hours, minutes, totalMinutes, diffMs, isComplete: diffMs <= 0 };
}

export function isSchedulePending(schedule, now = new Date()) {
  const startDate = getScheduleStartDate(schedule);
  if (!startDate) return false;
  return startDate.getTime() > now.getTime();
}

export function formatScheduledStart(value, timezone = getBrowserTimezone()) {
  const date = getDateFromFirestoreValue(value);
  const normalizedTimezone = normalizeTimezone(timezone);
  if (!date) return "Start time not set";

  return new Intl.DateTimeFormat(undefined, {
    timeZone: normalizedTimezone,
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  }).format(date);
}
